import { FC, Fragment, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { RosterFilters } from "@services/engagements/population/commands/cmdGetSegmentPopulation";
import { PopulationService } from "@services/engagements/population/populationService";
import { SegmentPopulation } from "@models/population.model";
import { CHANNEL_HOLDOUT, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import { PopulationRouteView } from "../phases/campaign/Population/PopulationRouteView";
import Disclosure from "./Disclosure";
import HoldoutsSection from "./HoldoutsSection";
import StepPanel from "./StepPanel";
import TieredConversions from "./TieredConversions";

const service = new PopulationService();
const PAGE_SIZE = 10;

/** One headline figure. Undefined renders a dash: still loading, or an older server. */
const Stat: FC<{ label: string; value: number | undefined; tone?: "control" | "converted" }> = ({
  label,
  value,
  tone,
}) => (
  <div className="population-stats__stat">
    {/* One line, always: a long label wrapping made its tile taller than its neighbours. */}
    <div className="population-stats__label" title={label}>{label}</div>
    <div className={`population-stats__value${tone ? ` population-stats__value--${tone}` : ""}`}>
      {value != null ? value.toLocaleString() : "—"}
    </div>
  </div>
);

/** How often the page re-reads itself while a rebuild is running. */
const REBUILD_POLL_MS = 10_000;

type Filter = "all" | "served" | "control" | "test";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "Everyone" },
  { key: "served", label: "Served" },
  { key: "control", label: "Held out" },
  { key: "test", label: "Test" },
];

// Served/Held-out are real-people concepts, so both exclude test rows; "Test"
// shows only synthetic rows from ops test files (never audienced or converted).
const FILTER_PARAMS: Record<Filter, { control: string | null; test: string | null }> = {
  all: { control: null, test: null },
  served: { control: "served", test: "real" },
  control: { control: "control", test: null },
  test: { control: null, test: "test" },
};

// These are file dates ("2026-01-01"), not instants. Parsing one bare puts it at UTC
// midnight, which localizes BACKWARDS — a Pacific viewer saw "Dec 31, 2025". The explicit
// midnight makes it parse in the viewer's own zone, so the date reads as written.
const formatDate = (value: string | null) =>
  value
    ? new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      })
    : "—";

interface Props {
  populationSegmentId: number;
  /** Holdout assignment changes what the setup status reports. */
  onChanged: () => void;
  /** Population-wide actions (Reprocess all files), under the headline figures. */
  actions?: ReactNode;
  /** The Links section's content: what this population is wired to. */
  linksPanel?: ReactNode;
}

/*
    Who is in this population and who is held out. Reads the segment directly
    rather than a launch, so it works before any campaign exists — the holdout
    is a property of the population, not of a campaign targeting it.

    Results first, settings after (MPN-2077): the figures, conversions by step for
    Treatment and Held Out, then the same per channel holdout; then Reprocess, the
    Holdouts section with both tiers, and Links and Members folded away.
*/
const PopulationRoster: FC<Props> = ({
  populationSegmentId,
  onChanged,
  actions,
  linksPanel,
}) => {
  const channelHoldoutOn = useFeatureFlag(CHANNEL_HOLDOUT);
  const [data, setData] = useState<SegmentPopulation | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(0);
  // Which row's "Steps Taken" action history is open (MPN-1602). One at a time,
  // like the campaign population list.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft policy, so the slider can be moved without firing an assignment per tick.
  // Column filters (MPN-1613). Held together so clearing is one action and the paging
  // reset below has a single dependency to watch.
  const [columnFilters, setColumnFilters] = useState<RosterFilters>({});
  const hasColumnFilter = Object.values(columnFilters).some(Boolean);

  // Any filter change returns to page one. Staying on page 4 of a list that now has one
  // page shows an empty table and reads as "no matches".
  const setFilterValue = (key: keyof RosterFilters, value: string) => {
    setPage(0);
    setColumnFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };
  const clearColumnFilters = () => {
    setPage(0);
    setColumnFilters({});
  };

  const [draftEnabled, setDraftEnabled] = useState(false);
  const [draftPct, setDraftPct] = useState(5);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = FILTER_PARAMS[filter];
      const result = await service.getSegmentPopulation(
        populationSegmentId,
        PAGE_SIZE,
        page * PAGE_SIZE,
        params.control,
        params.test,
        columnFilters,
      );
      setData(result);
      setDraftEnabled(result.control_group_enabled);
      setDraftPct(result.control_group_pct);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this population");
    } finally {
      setIsLoading(false);
    }
  }, [populationSegmentId, page, filter, columnFilters]);

  useEffect(() => {
    void load();
  }, [load]);

  // While a rebuild runs, re-read so the banner and figures move with it, and so the
  // page settles on the final numbers when it ends.
  const rebuilding = data?.active_rebuild != null;
  useEffect(() => {
    if (!rebuilding) return;
    const timer = setInterval(() => void load(), REBUILD_POLL_MS);
    return () => clearInterval(timer);
  }, [rebuilding, load]);
  // A finished rebuild changes the setup status too, so tell the host once it ends.
  const wasRebuilding = useRef(false);
  useEffect(() => {
    if (wasRebuilding.current && !rebuilding) onChanged();
    wasRebuilding.current = rebuilding;
  }, [rebuilding, onChanged]);

  const applyHoldout = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await service.setSegmentControlGroup(populationSegmentId, draftEnabled, draftPct);
      setPage(0);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update the holdout");
    } finally {
      setIsSaving(false);
    }
  };

  const applyChannelHoldout = async (channel: string, enabled: boolean, pct: number) => {
    setIsSaving(true);
    setError(null);
    try {
      await service.setSegmentChannelHoldout(populationSegmentId, channel, enabled, pct);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update the channel holdout");
    } finally {
      setIsSaving(false);
    }
  };


  const isDirty =
    data != null && (draftEnabled !== data.control_group_enabled || draftPct !== data.control_group_pct);

  const pageCount = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // Whether this server sends holdout figures at all (MPN-1615). Keyed on the array
  // being present, not on it being non-empty: a population with no holdout conversions
  // still gets every configured step back at zero, and "0 conv" is the answer Ops came
  // for. Undefined means an older server, and then the page shows what it always did.
  const holdoutAvailable = data?.conversions_by_step_holdout !== undefined;

  return (
    <div className="data-file__section">
      <div className="data-file__body">
        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        {/* Every figure below is read live, so during a rebuild it is partial and
            still climbing. On 9/29 a mid-rebuild screenshot was taken for the final
            numbers (MPN-1943). */}
        {data?.active_rebuild && (
          <div className="alert alert-warning py-2 small d-flex align-items-center gap-2">
            <i className="bi bi-arrow-repeat" />
            <span>
              Reprocess all in progress
              {data.active_rebuild.progress_message ? `: ${data.active_rebuild.progress_message}` : ""}.
              {" "}These numbers are partial until it finishes.
            </span>
            <Link className="ms-auto" to={`/jobs/${data.active_rebuild.job_id}`}>View job</Link>
          </div>
        )}

        {/* People and events are separate groups so a count of conversions never sits
            beside a count of people (MPN-1943): one member converting on three steps is
            one "member converted" and three "conversions". */}
        <div className="population-stats-groups">
          <div className="population-stats-group">
            <div className="population-stats-group__title">Members</div>
            <div className="population-stats">
              <Stat label="Total members" value={data?.total_population} />
              <Stat label="Served" value={data?.served_count} />
              <Stat label="Held out" value={data?.control_count} tone="control" />
              {data != null && data.test_count > 0 && <Stat label="Test rows" value={data.test_count} />}
              {/* The contracted ceiling, beside the count it caps (MPN-1718). Absent
                  when no ceiling is recorded: a dash here would read as a measurement,
                  and these are all measurements. */}
              {data?.contract_list_size != null && (
                <Stat
                  label={
                    data.contract_sized_by === "CAMPAIGN"
                      ? "Contract list size (per campaign)"
                      : "Contract list size"
                  }
                  value={data.contract_list_size}
                />
              )}
            </div>
          </div>
          <div className="population-stats-group">
            <div className="population-stats-group__title">Conversions{holdoutAvailable ? " — Treatment" : ""}</div>
            <div className="population-stats">
              <Stat label="Members converted" value={data?.total_converters} tone="converted" />
              <Stat label="Total conversions" value={data?.total_conversions} tone="converted" />
              <Stat label="Pre-conversions" value={data?.total_preconversions} />
            </div>
          </div>
          {holdoutAvailable && (
            <div className="population-stats-group population-stats-group--holdout">
              <div className="population-stats-group__title">Conversions — Holdout</div>
              <div className="population-stats">
                <Stat label="Members converted" value={data?.total_converters_holdout} tone="converted" />
                <Stat label="Total conversions" value={data?.total_conversions_holdout} tone="converted" />
              </div>
            </div>
          )}
        </div>
        <div className="data-file__note mb-3">
          A member converting on several steps is one member converted and several conversions.
        </div>

        {data != null && (data.conversions_by_step ?? []).length === 0 && (
          <div className="data-file__note mb-3">
            No priced steps for this population yet.
          </div>
        )}

        {data != null && (data.conversions_by_step ?? []).length > 0 && (
          /* Two panels when the server sends the holdout, one when it does not
             (MPN-1615). Both lists are zero-padded server-side with every configured
             step, in sequence order, so the rows line up across the two columns. */
          <div className={holdoutAvailable ? "population-steps-pair" : undefined}>
            <StepPanel
              title={holdoutAvailable ? "Conversions by step — Treatment" : "Conversions by step"}
              steps={data.conversions_by_step ?? []}
            />
            {holdoutAvailable && (
              <StepPanel
                title="Conversions by step — Held Out"
                steps={data.conversions_by_step_holdout ?? []}
                holdout
              />
            )}
          </div>
        )}

        {/* Under the pair, the same panels for each applied channel holdout's two arms
            (MPN-2077): the results first, the settings that produced them further down. */}
        {channelHoldoutOn &&
          (data?.channel_holdouts ?? [])
            .filter((h) => h.enabled)
            .map((h) => <TieredConversions key={h.channel} holdout={h} />)}

        {actions}

        {data != null && (
          <HoldoutsSection
            data={data}
            draft={{
              enabled: draftEnabled,
              pct: draftPct,
              onEnabledChange: setDraftEnabled,
              onPctChange: setDraftPct,
              isDirty,
              onApply: () => void applyHoldout(),
            }}
            saving={isSaving}
            channelHoldoutsOn={channelHoldoutOn}
            onApplyChannel={applyChannelHoldout}
          />
        )}


        {linksPanel && <Disclosure title="Links">{linksPanel}</Disclosure>}

        <Disclosure title="Members">
        {hasColumnFilter && (
          <button
            type="button"
            className="roster-filter__clear"
            onClick={clearColumnFilters}
          >
            Clear column filters
          </button>
        )}

        <div className="population-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`data-file-detail__tab${filter === f.key ? " data-file-detail__tab--active" : ""}`}
              onClick={() => {
                setFilter(f.key);
                setPage(0);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading && !data ? (
          <div className="data-file__note py-4 text-center">Loading population…</div>
        ) : !data ? (
          <div className="data-file__note py-4 text-center">
            Could not load this population.
          </div>
        ) : (
          <table className="population-table">
            <thead>
              <tr>
                <th>
                  Consumer
                  <input
                    type="search"
                    className="roster-filter roster-filter--text"
                    placeholder="Name or id"
                    aria-label="Filter by consumer"
                    value={columnFilters.consumerQ ?? ""}
                    onChange={(e) => setFilterValue("consumerQ", e.target.value)}
                  />
                </th>
                {/* Status is filtered by the Everyone / Served / Held out / Test row
                    above. A header dropdown here would be a second control for the same
                    thing, disagreeing with the first whenever they drift. */}
                <th>Status</th>
                <th>Status updated</th>
                <th>First seen</th>
                <th>Last seen</th>
                <th style={{ textAlign: "right" }}>
                  Steps taken
                  <select
                    className="roster-filter"
                    aria-label="Filter by action taken"
                    value={columnFilters.traceType ?? ""}
                    onChange={(e) => setFilterValue("traceType", e.target.value)}
                  >
                    <option value="">Any action</option>
                    <option value="CONVERSION">Converted</option>
                    <option value="PRECONVERT">Pre-converted</option>
                    <option value="REVERSION">Reverted</option>
                  </select>
                </th>
                <th style={{ width: "2rem" }} aria-label="Expand" />
              </tr>
            </thead>
            <tbody>
              {/*
                  An empty result is a row, not a replacement for the table. Rendering the
                  message instead of the table took the header with it, and the filters
                  live in the header -- so filtering to nothing removed the only way to
                  stop filtering (MPN-1613).
              */}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="data-file__note py-4 text-center">
                    {data.total_population === 0
                      ? "Nobody in this population yet — process a data file to load it."
                      : "No one matches this filter."}
                  </td>
                </tr>
              )}
              {data.items.map((row) => {
                const isExpanded = expandedId === row.consumer_id;
                return (
                  <Fragment key={row.consumer_id}>
                    <tr
                      onClick={() =>
                        setExpandedId((prev) => (prev === row.consumer_id ? null : row.consumer_id))
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td>{row.name}</td>
                      <td>
                        {row.is_test ? (
                          <span
                            className="population-table__control"
                            title="Synthetic row from an ops test file — never added to audiences, never counted as a conversion."
                          >
                            Test
                          </span>
                        ) : row.is_control ? (
                          <span className="population-table__control">Held out</span>
                        ) : (
                          <span className="population-table__served">Served</span>
                        )}
                      </td>
                      <td>{formatDate(row.status_as_of)}</td>
                      <td>{formatDate(row.first_as_of)}</td>
                      <td>{formatDate(row.current_as_of)}</td>
                      <td style={{ textAlign: "right" }}>
                        {/* Older servers omit the count; the history still expands. */}
                        {row.steps_taken != null ? row.steps_taken.toLocaleString() : "—"}
                      </td>
                      <td style={{ textAlign: "center", color: "#64748b" }}>
                        <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          {/* Full action history — pre-convert / conversion (step ·
                              dimension) / reversion, each with the as_of date of the
                              file that counted it (MPN-1602). */}
                          <PopulationRouteView
                            populationSegmentId={populationSegmentId}
                            consumerId={row.consumer_id}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {data && data.total > PAGE_SIZE && (
          <div className="population-pager">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isLoading}
              style={{ fontSize: "0.72rem" }}
            >
              Previous
            </button>
            <span className="data-file__note">
              Page {page + 1} of {pageCount}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= pageCount || isLoading}
              style={{ fontSize: "0.72rem" }}
            >
              Next
            </button>
          </div>
        )}
        </Disclosure>
      </div>
    </div>
  );
};

export default PopulationRoster;
